import { useMemo, useState } from "react";
import { briefing, channels } from "@/mocks/home";

const toneMap: Record<string, { chip: string; dot: string }> = {
  live: { chip: "bg-primary-100 text-primary-700", dot: "bg-primary-500" },
  wait: { chip: "bg-secondary-100 text-secondary-800", dot: "bg-secondary-400" },
  doubt: { chip: "bg-accent-100 text-accent-800", dot: "bg-accent-500" },
};

export default function Briefing() {
  const [active, setActive] = useState("全部");

  const items = useMemo(
    () => (active === "全部" ? briefing : briefing.filter((b) => b.channel === active)),
    [active],
  );

  return (
    <section id="briefing" className="border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-12 md:py-16">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-primary-600">
              <span className="h-4 w-1 rounded bg-primary-500" />
              下半区 · 更新简报
            </p>
            <h2 className="mt-3 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
              近 24 小时跟进速览
            </h2>
          </div>
          <p className="text-sm text-foreground-500">
            更密一点的实时更新列表，按时间倒序排放，方便你快速扫出今天的新动静。
          </p>
        </div>

        {/* 频道过滤 */}
        <div className="mt-7 flex flex-wrap items-center gap-2">
          {channels.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors ${
                active === c
                  ? "bg-foreground-950 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <div className="grid grid-cols-1 gap-x-4 gap-y-0 divide-y divide-background-100 md:grid-cols-2 md:divide-x md:divide-y-0">
            {items.slice(0, Math.ceil(items.length / 2)).map((b) => <BriefRow key={b.id} item={b} toneMap={toneMap} />)}
            <div className="md:divide-y md:divide-background-100">
              {items.slice(Math.ceil(items.length / 2)).map((b) => <BriefRow key={b.id} item={b} toneMap={toneMap} />)}
            </div>
          </div>
          {items.length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-foreground-500">该频道暂时没有新的更新，稍后再来看看。</p>
          )}
        </div>
      </div>
    </section>
  );
}

function BriefRow({
  item,
  toneMap: map,
}: {
  item: { id: number; channel: string; title: string; status: string; tone: string; time: string };
  toneMap: Record<string, { chip: string; dot: string }>;
}) {
  const meta = map[item.tone];
  return (
    <div className="flex items-start gap-3 px-4 py-3.5 transition-colors hover:bg-background-100/60 md:px-5">
      <span className="mt-0.5 shrink-0 whitespace-nowrap rounded-md bg-secondary-100 px-2 py-1 text-[11px] font-semibold text-secondary-800">
        {item.channel}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-semibold text-foreground-900">{item.title}</p>
        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-foreground-400">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-semibold ${meta.chip}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
            {item.status}
          </span>
          <span>{item.time}</span>
        </div>
      </div>
      <i className="ri-arrow-right-up-line mt-1 text-sm text-foreground-300" />
    </div>
  );
}